import React, { lazy } from 'react';
import { motion } from 'framer-motion';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 48
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 1.1,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
const images = {
  living: 'https://cdn.magicpatterns.com/uploads/ftxFs7wvjnHRavz7jmQFGF/10.png',
  dining: 'https://cdn.magicpatterns.com/uploads/u64xa7nfqixxQJoS4wgcBN/11.png',
  kitchen: 'https://cdn.magicpatterns.com/uploads/cR4hKEkprNwNU5E88h8xtx/12.png',
  lounge: 'https://cdn.magicpatterns.com/uploads/4AJa3SieiyEfhsDZLRYnTS/13.png',
  bedroom: 'https://cdn.magicpatterns.com/uploads/9SVdvNg6Ny3TJz11ugJFf7/14.png',
  bath: 'https://cdn.magicpatterns.com/uploads/ogep1UmqLMut3eavzDoDrL/15.png'
};
interface FloatImageProps {
  src: string;
  alt: string;
  className?: string;
  ratio?: string;
}
function FloatImage({
  src,
  alt,
  className = '',
  ratio = 'aspect-[16/10]'
}: FloatImageProps) {
  return <motion.figure variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-90px'
  }} className={`group overflow-hidden ${className}`} data-id="element-38" data-mp="205wdg9uxa37j">
      <img src={src} alt={alt} loading="lazy" className={`w-full h-full object-cover ${ratio} transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]`} data-id="element-39" data-mp="1qcssud0gqrdf" />
    </motion.figure>;
}
function FloatText({
  eyebrow,
  children,
  className = ''
}: {
  eyebrow: string;
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-90px'
  }} className={className} data-id="element-40" data-mp="17doohhjv9ls3">
      <p className="text-[0.7rem] tracking-[0.28em] uppercase text-text-muted font-light mb-3" data-id="element-41" data-mp="31rokczk7knbe">
        {eyebrow}
      </p>
      <p className="font-heading text-xl md:text-2xl font-light leading-snug text-primary" data-id="element-42" data-mp="36ravn3gh7thk">
        {children}
      </p>
    </motion.div>;
}
export function FeatureGallery() {
  return <section className="py-16 md:py-32 px-6 md:px-14 bg-bg-warm overflow-hidden relative z-10" data-id="element-43" data-mp="30ask0c01tbj4">
      <div className="max-w-[1400px] mx-auto" data-id="element-44" data-mp="2vb68c6zvc3cf">
        {/* Section intro */}
        <motion.header variants={fadeUp} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} className="mb-14 md:mb-24 max-w-2xl" data-id="element-45" data-mp="2vd983iqtpauc">
          <p className="text-xs tracking-[0.28em] text-text-muted font-light mb-4 uppercase" data-id="element-46" data-mp="3j21zr30ewwj5">
            Thoughtful design
          </p>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight text-primary leading-tight" data-id="element-47" data-mp="3e87is7c6yvnr">
            Designed around light, space, and the way you live
          </h2>
        </motion.header>

        {/* Dispersed, floating composition */}
        <div className="flex flex-col gap-6 md:gap-0" data-id="element-48" data-mp="5tp2jic3k9t2">
          {/* Block 1 — wide living image pulled right, text floating left */}
          <div className="md:grid md:grid-cols-12 md:gap-10 md:items-end" data-id="element-49" data-mp="1ojbctiok6tbn">
            <FloatText eyebrow="Spacious interior" className="hidden md:block md:col-span-3 md:pb-6" data-id="element-50" data-mp="1tgv2rfumcujf">
              Rooms that breathe, with an open plan that flows from one moment
              to the next.
            </FloatText>
            <FloatImage src={images.living} alt="Sunlit living room with floor-to-ceiling windows" className="md:col-span-9" ratio="aspect-[16/10]" data-id="element-51" data-mp="27f1j3i3iybvc" />
          </div>

          {/* Block 2 — dining image left, text floating right */}
          <div className="md:grid md:grid-cols-12 md:gap-10 md:mt-28 md:items-center" data-id="element-52" data-mp="2l6g1pntisb1v">
            <FloatImage src={images.dining} alt="Dining area opening onto greenery" className="md:col-span-7" ratio="aspect-[16/10]" data-id="element-53" data-mp="2q7dipx2smz1s" />
            <FloatText eyebrow="Considered design" className="hidden md:block md:col-span-4 md:col-start-9" data-id="element-54" data-mp="2hi6jk6hhfglv">
              Every sightline framed toward the garden and the light beyond.
            </FloatText>
          </div>

          {/* Block 3 — full-bleed kitchen band */}
          <FloatImage src={images.kitchen} alt="Chef's kitchen with warm timber cabinetry" className="md:mt-28" ratio="aspect-[16/9] md:aspect-[24/9]" data-id="element-55" data-mp="2maznis54mkww" />

          {/* Block 4 — two offset images at different heights */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-10 md:mt-28 md:items-start" data-id="element-56" data-mp="28ijt2fqajv7l">
            <FloatImage src={images.lounge} alt="Bright lounge with garden views" className="md:col-span-5 md:mt-24" ratio="aspect-[16/10]" data-id="element-57" data-mp="23ixdw0qwk4w5" />
            <div className="hidden md:block md:col-span-1" data-id="element-58" data-mp="3exas0dvut5yz" />
            <FloatImage src={images.bedroom} alt="Primary bedroom framing the treetops" className="md:col-span-6" ratio="aspect-[16/11]" data-id="element-59" data-mp="3mi4stnvdo7ho" />
          </div>

          {/* Block 5 — bathroom image left, text floating right */}
          <div className="md:grid md:grid-cols-12 md:gap-10 md:mt-28 md:items-center" data-id="element-60" data-mp="3his93bzszgjb">
            <FloatImage src={images.bath} alt="Spa-like bathroom with timber vanity and walk-in shower" className="md:col-span-8" ratio="aspect-[16/9]" data-id="element-61" data-mp="3hklfqraf20rb" />
            <FloatText eyebrow="Everyday calm" className="hidden md:block md:col-span-3 md:col-start-10" data-id="element-62" data-mp="awqkufr8gpv">
              A retreat for the small rituals, wrapped in warm light and stone.
            </FloatText>
          </div>
        </div>
      </div>
    </section>;
}